import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import isNumber from "lodash/isNumber";
import { parseDate } from "./dayjs-relative";

export function fromStringToDate(dateString: string): Date {
	return new Date(dateString);
}

export function fromUnixToDate(unix: number): Date {
	return new Date(unix * 1000);
}

export function unix(): number {
	return dayjs().unix();
}

export function today(): dayjs.Dayjs {
	return dayjs();
}

export function todayTz(): dayjs.Dayjs {
	dayjs.extend(utc);
	dayjs.extend(timezone);
	return dayjs();
}

/**
 * @description Convert a date string to a Unix timestamp
 *
 * @example
 * fromUnixToDateString(1632974400) // 02 Oct 2021
 */
export function fromUnixToDateString(unix: number): string {
	return fromUnixToDate(unix).toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
}

export function formatHumanReadableDate(date: string | number | Date) {
	let formatDate = date as Date;
	if (typeof date === "string") {
		formatDate = fromStringToDate(date);
	}
	if (typeof date === "number") {
		formatDate = fromUnixToDate(date);
	}
	const now = new Date();
	const diffMs = now.getTime() - formatDate.getTime();
	const diffMinutes = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);

	if (diffMinutes < 1) {
		return "Just now";
	}
	if (diffMinutes < 60) {
		return `${diffMinutes}m ago`; // Minutes ago
	}
	if (diffHours < 24) {
		return `${diffHours}h ago`; // Hours ago
	}
	if (diffDays > 0) {
		return formatDate.toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		});
	}

	return formatDate.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	});
}

export const currentYear = new Date().getFullYear();

export function fromYearToAge(year: number | string, suffix?: string) {
	const age = isNumber(year)
		? currentYear - year
		: dayjs().diff(parseDate(year, "YYYY-MM-DD"), "year");
	if (suffix) return age + suffix;
	return age.toString();
}

/**
 * @example
 * fromMsToTime(1731423626) // 10:00 AM
 */
export function fromMsToTime(inputMs: number): string {
	const date = new Date(inputMs * 1000);
	return date.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
	});
}

/**
 * @description Generate a custom formatted date string
 * @example
 * generateCustomFormattedDate() // 2021-09-30T10:00:00+0800
 */
export function getIsoDateTime() {
	dayjs.extend(utc);
	dayjs.extend(timezone);
	return dayjs().tz("Asia/Kuala_Lumpur").format();
}

/**
 * @description Format a date string to a custom format
 * @example
 * formatDateTime("2021-11-30T10:00:00+0800") // 26 Nov 2021
 */
export const formatDate = (date: string | Date) => {
	const formatDate = typeof date === "string" ? fromStringToDate(date) : date;
	return formatDate.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

export const isSameDay = (date1: Date, date2: Date) => {
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
};

export const getTodayString = () => {
	return new Date().toDateString();
};

export const getWeeks = (
	date?: dayjs.ConfigType,
	format = "YYYY-MM-DD",
): string[] => {
	const startOfWeek = dayjs(date).startOf("week");
	const daysOfWeek = Array.from({ length: 7 }, (_, i) =>
		startOfWeek.add(i, "day"),
	);
	return daysOfWeek.map((d) => d.format(format));
};

export const getMonths = (
	date?: dayjs.ConfigType,
	format?: dayjs.OptionType,
) => {
	const today = dayjs(date, format);
	const startOfMonth = today.startOf("month");
	const daysInMonth = today.daysInMonth();

	const daysArray = Array.from({ length: daysInMonth }, (_, i) =>
		startOfMonth.add(i, "day"),
	);
	return daysArray;
};
